import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { usePropertyFilters } from '../../contexts/PropertyFilterContext';

// Ícones para os domínios (mesmo padrão das tabs)
import { Leaf, MapPin, FileText, Users, ClipboardList } from 'lucide-react'; // Mesmos ícones usados nas tabs

import {
  extractAllDomainStatus,
  extractCarCodes,
  extractSigefCodes,
  extractNirfCodes,
  extractSncrCode,
  extractSncrIncraCode,
  countSigefParcels,
} from '../../utils/propertyUtils';
import DomainStatusChip from './ui/DomainStatusChip';

/**
 * Componente para exibir os status de todos os domínios da propriedade
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.propertyData - Dados da propriedade
 * @param {Function} props.onDomainClick - Função a ser chamada ao clicar em um domínio
 * @returns {JSX.Element} Bloco de status de domínio
 */
const PropertyDomainStatus = ({ propertyData, onDomainClick }) => {
  // Usar contexto de filtros para possibilitar a seleção de códigos específicos
  const { setSelectedCarCodes, setSelectedSigefCodes, setSelectedCafirCodes } =
    usePropertyFilters();

  // Extração de dados dos domínios (base)
  const domainStatusBase = extractAllDomainStatus(propertyData);
  const _hasAnyDomainData = (k) => {
    const p = propertyData?.properties || {};
    const v = p?.[k] ?? p?.[k?.toUpperCase?.()];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return !!v;
  };
  // Status final diretamente derivado do snapshot (sem máquina de estados)
  const domainStatus = domainStatusBase;
  const uiDomainStatus = domainStatus;
  const carCodes = extractCarCodes(propertyData);
  const sigefCodes = extractSigefCodes(propertyData);
  const nirfCodes = extractNirfCodes(propertyData);
  const sncrCode = extractSncrCode(propertyData);
  const sncrIncraCode = extractSncrIncraCode(propertyData);
  const sigefParcelCount = countSigefParcels(propertyData);

  // Logs para debug (verbosos somente quando VITE_DEBUG_VERBOSE=true)
  const VERBOSE = import.meta.env.VITE_DEBUG_VERBOSE === 'true';
  if (VERBOSE) {
    // eslint-disable-next-line no-console
    console.log('[DEBUG] PropertyDomainStatus - Status extraídos:', {
      car: domainStatus.car,
      sigef: domainStatus.sigef,
      cafir: domainStatus.cafir,
      sncr: domainStatus.sncr,
    });
    // eslint-disable-next-line no-console
    console.log('[DEBUG] PropertyDomainStatus - Códigos extraídos:', {
      carCodes,
      sigefCodes,
      nirfCodes,
      sncrCode,
    });
  }

  // Manipular clique em códigos específicos
  const handleCodeClick = (e, codeType, code) => {
    // Evita que o clique propague para o container
    e.stopPropagation();

    // Atualizar o código selecionado no contexto com base no tipo
    switch (codeType.toLowerCase()) {
      case 'car':
        setSelectedCarCodes([code]);
        break;
      case 'sigef':
        setSelectedSigefCodes([code]);
        break;
      case 'cafir':
        setSelectedCafirCodes([code]);
        break;
      default:
        break;
    }

    // Garantir que a tab do domínio correspondente esteja aberta
    if (onDomainClick && typeof onDomainClick === 'function') {
      onDomainClick(codeType.toLowerCase());
    }
  };

  // Extrai área do SNCR se disponível
  const extractSncrArea = () => {
    if (propertyData?.properties?.SNCR?.area_total) {
      return `Área: ${propertyData.properties.SNCR.area_total} ha`;
    }
    if (propertyData?.properties?.sncr?.area_total) {
      return `Área: ${propertyData.properties.sncr.area_total} ha`;
    }
    return null;
  };

  const sncrArea = extractSncrArea();

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Grid container spacing={1} columns={{ xs: 12, sm: 12, md: 24 }} sx={{ px: 0.5 }}>
        {/* CAR */}
        <Grid
          size={{ xs: 12, sm: 6, md: 4 }}
          sx={{
            flexGrow: { xs: 1, md: 0 },
            flexBasis: { xs: '100%', md: 'auto' },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: 'unset' },
          }}
        >
          <Box
            sx={{
              p: 1,
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              transition: 'background-color 0.3s',
            }}
            onClick={() => onDomainClick && onDomainClick('car')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Leaf size={16} className="text-green-600 mr-1" />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                CAR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <DomainStatusChip domain="car" status={uiDomainStatus.car} onClick={onDomainClick} />
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {carCodes.length > 0
                ? carCodes.map((code, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        p: 0.5,
                        mb: 0.5,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 0.5,
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(39, 174, 96, 0.1)' },
                      }}
                      title={code}
                      onClick={(e) => handleCodeClick(e, 'car', code)}
                    >
                      {code}
                    </Typography>
                  ))
                : null}
            </Box>
          </Box>
        </Grid>

        {/* SIGEF */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              p: 1,
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              transition: 'background-color 0.3s',
            }}
            onClick={() => onDomainClick && onDomainClick('sigef')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MapPin size={18} className="text-blue-600 mr-1" />
              <Typography variant="subtitle2" fontWeight="bold">
                SIGEF
              </Typography>
              {sigefParcelCount > 0 && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ ml: 1, fontSize: '0.65rem', color: 'text.secondary' }}
                >
                  {sigefParcelCount} parcela{sigefParcelCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DomainStatusChip
                domain="sigef"
                status={uiDomainStatus.sigef}
                onClick={onDomainClick}
              />
            </Box>

            <Box>
              {sigefCodes.length > 0
                ? sigefCodes.map((code, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        p: 0.5,
                        mb: 0.5,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' },
                      }}
                      onClick={(e) => handleCodeClick(e, 'sigef', code)}
                    >
                      {code}
                    </Typography>
                  ))
                : null}
            </Box>
          </Box>
        </Grid>

        {/* CAFIR */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              p: 1,
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              transition: 'background-color 0.3s',
            }}
            onClick={() => onDomainClick && onDomainClick('cafir')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FileText size={18} className="text-orange-600 mr-1" />
              <Typography variant="subtitle2" fontWeight="bold">
                CAFIR
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DomainStatusChip
                domain="cafir"
                status={uiDomainStatus.cafir}
                onClick={onDomainClick}
              />
            </Box>

            <Box>
              {nirfCodes.length > 0
                ? nirfCodes.map((code, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        p: 0.5,
                        mb: 0.5,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(230, 126, 34, 0.1)' },
                      }}
                      onClick={(e) => handleCodeClick(e, 'cafir', code)}
                    >
                      {code}
                    </Typography>
                  ))
                : null}
            </Box>
          </Box>
        </Grid>

        {/* SNCR */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              p: 1,
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              transition: 'background-color 0.3s',
            }}
            onClick={() => onDomainClick && onDomainClick('sncr')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Users size={18} className="text-purple-600 mr-1" />
              <Typography variant="subtitle2" fontWeight="bold">
                SNCR
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DomainStatusChip
                domain="sncr"
                status={uiDomainStatus.sncr}
                onClick={onDomainClick}
              />
            </Box>

            <Box>
              {sncrCode ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    p: 0.5,
                    mb: 0.5,
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(142, 68, 173, 0.1)' },
                  }}
                  onClick={(e) => handleCodeClick(e, 'sncr', sncrCode)}
                >
                  {sncrCode}
                </Typography>
              ) : null}

              {sncrIncraCode ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    p: 0.5,
                    mb: 0.5,
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.secondary',
                  }}
                >
                  {sncrIncraCode}
                </Typography>
              ) : null}

              {sncrArea && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    mt: 1,
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                >
                  {sncrArea}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* CCIR */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box
            sx={{
              p: 1,
              height: '100%',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              transition: 'background-color 0.3s',
            }}
            onClick={() => onDomainClick && onDomainClick('ccir')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ClipboardList size={18} className="text-teal-600 mr-1" />
              <Typography variant="subtitle2" fontWeight="bold">
                CCIR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DomainStatusChip
                domain="ccir"
                status={uiDomainStatus.ccir}
                onClick={onDomainClick}
              />
            </Box>
            <Box>
              {propertyData?.properties &&
                (() => {
                  const props = propertyData.properties;
                  const key = Object.keys(props).find((k) => {
                    const lower = k.toLowerCase();
                    return lower === 'serpro_ccir' || lower === 'ccir';
                  });
                  const record = key ? props[key] : null;
                  const entry = Array.isArray(record) ? record.find(Boolean) : record;
                  const codigoIncra = entry?.codigo_imovel_incra;
                  if (!codigoIncra) return null;
                  return (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        p: 0.5,
                        mb: 0.5,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {codigoIncra}
                    </Typography>
                  );
                })()}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default PropertyDomainStatus;
